import { Link } from "@/lib/router-compat";
import { ChevronRight, Phone, AlertCircle } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import KeyTakeaways from "@/components/sections/KeyTakeaways";
import ClinicalReviewNote from "@/components/ClinicalReviewNote";
import { RelatedPages } from "@/components/RelatedPages";
import { SITE_URL, ORG_ID } from "@/components/schemaConstants";

const faqs = [
  {
    q: "What does it mean when depression treatment isn't working?",
    a: "If your symptoms have not adequately improved after an appropriate course of treatment — such as one or more antidepressant medications taken at a therapeutic dose for a sufficient duration, with or without psychotherapy — a clinician may evaluate whether your depression meets common definitions of treatment-resistant depression and discuss other evidence-based options. This is a clinical determination made by a qualified healthcare professional.",
  },
  {
    q: "What should I do if my antidepressant isn't working?",
    a: "Do not stop your medication on your own. Talk to your prescribing clinician. They can review whether the dose and duration were adequate, check for contributing factors like comorbid conditions or medication interactions, and discuss options such as medication optimization, augmentation, psychotherapy, or non-medication treatments like TMS or SAINT®.",
  },
  {
    q: "What comes after medication and therapy for depression?",
    a: "When medication and therapy have not produced adequate improvement, options a psychiatrist may discuss include medication adjustments or augmentation, FDA-cleared TMS, the accelerated SAINT® protocol, FDA-approved SPRAVATO® (esketamine), or, for severe cases, ECT. The right next step depends on your individual history and is determined by a qualified clinician.",
  },
  {
    q: "Is there hope after multiple depression treatments fail?",
    a: "Yes. Having tried multiple treatments without lasting relief does not mean no options remain. Treatment-resistant depression has a range of evidence-based approaches, including neuromodulation treatments like TMS and SAINT® that are specifically indicated for adults whose depression has not improved on antidepressant medication. A psychiatric evaluation can help identify the next appropriate step.",
  },
  {
    q: "What is treatment-resistant depression?",
    a: "Treatment-resistant depression is major depressive disorder that has not responded adequately to at least two antidepressant medication trials of adequate dose and duration in the current episode. It describes treatment history, not a separate diagnosis, and it does not mean the depression cannot be treated.",
  },
];

const schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalWebPage",
      "@id": `${SITE_URL}/depression-treatment-not-working#webpage`,
      name: "What Can I Do When Depression Treatment Isn't Working?",
      url: `${SITE_URL}/depression-treatment-not-working`,
      description:
        "What to do when antidepressants, therapy, or other depression treatments aren't working. Options, next steps, and when to seek help.",
      inLanguage: "en-US",
      publisher: { "@id": ORG_ID },
      audience: { "@type": "MedicalAudience", audienceType: "Patient" },
      mainEntity: {
        "@type": "FAQPage",
        "@id": `${SITE_URL}/depression-treatment-not-working#faq`,
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${SITE_URL}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Depression Treatment Not Working",
          item: `${SITE_URL}/depression-treatment-not-working`,
        },
      ],
    },
  ],
};

const related = [
  {
    title: "When Antidepressants Don't Work",
    href: "/learn/when-antidepressants-dont-work",
    description: "What to try when medication isn't enough",
  },
  {
    title: "What Is Treatment-Resistant Depression?",
    href: "/learn/what-is-treatment-resistant-depression",
    description: "Definition and options",
  },
  {
    title: "SAINT® Accelerated TMS",
    href: "/saint",
    description: "5-day fMRI-guided TMS for eligible adults",
  },
  {
    title: "TMS Therapy",
    href: "/services/tms",
    description: "FDA-cleared TMS for depression",
  },
  {
    title: "SPRAVATO®",
    href: "/services/spravato",
    description: "FDA-approved esketamine for TRD",
  },
  {
    title: "Am I a Candidate?",
    href: "/am-i-a-candidate",
    description: "Request a consultation",
  },
];

const DepressionTreatmentNotWorking = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="When Depression Treatment Isn't Working | Neuro Wellness"
        description="What to do when antidepressants, therapy, or other depression treatments aren't working. Understand treatment-resistant depression and your options."
        canonical="/depression-treatment-not-working"
        schemaJson={schema}
      />
      <Navbar />
      <main className="pt-20">
        {/* Crisis banner */}
        <div className="bg-accent/10 border-b border-accent/20">
          <div className="mx-auto max-w-4xl px-4 py-3 flex items-center gap-2 text-sm text-foreground/90">
            <AlertCircle className="h-4 w-4 shrink-0 text-accent" />
            <p>
              If you are thinking about harming yourself, call or text{" "}
              <a
                href="tel:988"
                className="font-bold text-accent underline underline-offset-2"
              >
                988
              </a>{" "}
              (Suicide &amp; Crisis Lifeline, 24/7) or go to your nearest
              emergency room.
            </p>
          </div>
        </div>

        {/* Hero */}
        <section className="border-b border-border bg-card/30">
          <div className="mx-auto max-w-4xl px-4 py-12 sm:py-16">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-1 text-xs text-muted-foreground">
                <li>
                  <Link to="/" className="hover:text-accent">
                    Home
                  </Link>
                </li>
                <ChevronRight className="h-3 w-3 text-muted-foreground/50" />
                <li className="font-medium text-foreground">
                  Depression Treatment Not Working
                </li>
              </ol>
            </nav>
            <h1 className="font-headings text-3xl font-extrabold leading-tight text-primary sm:text-4xl">
              What Can I Do When Depression Treatment Isn&apos;t Working?
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-foreground">
              If you have tried antidepressant medication, therapy, or both and
              your symptoms have not adequately improved, you are not out of
              options. Persistent symptoms after appropriate treatment may lead
              a clinician to evaluate for treatment-resistant depression and
              discuss other evidence-based approaches. The next step is a
              conversation with a qualified healthcare professional who can
              review your history and recommend what may be appropriate for you.
            </p>
          </div>
        </section>

        {/* Key Takeaways */}
        <section className="py-10">
          <div className="mx-auto max-w-4xl px-4">
            <KeyTakeaways
              items={[
                {
                  text: "Persistent symptoms after adequate treatment may indicate treatment-resistant depression.",
                },
                {
                  text: "Do not stop medication on your own — talk to your prescribing clinician first.",
                },
                {
                  text: "Options include medication adjustments, psychotherapy, TMS, SAINT®, SPRAVATO®, and ECT.",
                },
                {
                  text: "Eligibility for any treatment is determined by a qualified healthcare professional.",
                },
                {
                  text: "Having tried multiple treatments does not mean no options remain.",
                },
              ]}
            />
          </div>
        </section>

        {/* Main content */}
        <section className="py-6 sm:py-10">
          <div className="mx-auto max-w-4xl px-4 space-y-10">
            <div>
              <h2 className="font-headings text-2xl font-bold text-primary sm:text-3xl">
                When Medication Hasn&apos;t Worked
              </h2>
              <p className="mt-3 text-foreground/85 leading-relaxed">
                If one or more antidepressant medications have not produced
                adequate improvement, a psychiatrist can review whether each
                trial was at a therapeutic dose for a sufficient duration, check
                for contributing factors such as comorbid anxiety, bipolar
                disorder, or substance use, and discuss options including
                medication optimization, augmentation, or non-medication
                treatments.{" "}
                <Link
                  to="/learn/when-antidepressants-dont-work"
                  className="font-medium text-accent underline underline-offset-2"
                >
                  Learn what to try when antidepressants don&apos;t work
                </Link>
                .
              </p>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary sm:text-3xl">
                When Therapy Hasn&apos;t Worked
              </h2>
              <p className="mt-3 text-foreground/85 leading-relaxed">
                If psychotherapy alone has not produced adequate improvement, it
                does not mean therapy has failed — it may mean that a
                combination approach is needed. Depression that has not
                responded to therapy may benefit from adding medication, or from
                neuromodulation treatments like TMS or SAINT® that address brain
                circuitry directly. Discuss with your clinician whether a
                combined approach is appropriate.
              </p>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary sm:text-3xl">
                When Medication and Therapy Haven&apos;t Worked
              </h2>
              <p className="mt-3 text-foreground/85 leading-relaxed">
                When both medication and therapy have not produced adequate
                relief, a psychiatrist may evaluate for treatment-resistant
                depression and discuss neuromodulation and interventional
                options. These include FDA-cleared TMS, the accelerated SAINT®
                protocol, and FDA-approved SPRAVATO® (esketamine). For severe or
                psychotic depression, ECT remains an option with the highest
                remission rates but with anesthesia and cognitive
                considerations.
              </p>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary sm:text-3xl">
                What Treatment-Resistant Depression Means
              </h2>
              <p className="mt-3 text-foreground/85 leading-relaxed">
                Treatment-resistant depression is major depressive disorder that
                has not responded adequately to at least two antidepressant
                medication trials of adequate dose and duration in the current
                episode. It describes treatment history, not a separate
                diagnosis, and it does not mean the depression cannot be
                treated.{" "}
                <Link
                  to="/learn/what-is-treatment-resistant-depression"
                  className="font-medium text-accent underline underline-offset-2"
                >
                  Learn more about treatment-resistant depression
                </Link>
                .
              </p>
            </div>

            {/* Treatment options grid */}
            <div>
              <h2 className="font-headings text-2xl font-bold text-primary sm:text-3xl">
                Treatment Options to Discuss With Your Psychiatrist
              </h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {[
                  {
                    name: "TMS Therapy",
                    desc: "FDA-cleared, non-invasive brain stimulation for depression that has not responded to medication. ~36 sessions over 6 weeks.",
                    href: "/services/tms",
                  },
                  {
                    name: "SAINT® Accelerated TMS",
                    desc: "FDA-cleared, fMRI-guided, 5-day accelerated protocol for eligible adults with major depressive disorder.",
                    href: "/saint",
                  },
                  {
                    name: "SPRAVATO® (Esketamine)",
                    desc: "FDA-approved nasal spray for treatment-resistant depression, administered in-office with monitoring.",
                    href: "/services/spravato",
                  },
                  {
                    name: "Medication Management",
                    desc: "Review and optimization of current medications, augmentation strategies, and careful adjustments.",
                    href: "/services/medication-management",
                  },
                ].map((opt) => (
                  <Link
                    key={opt.name}
                    to={opt.href}
                    className="block rounded-xl border border-border bg-card p-5 shadow-soft transition-all hover:border-accent/40"
                  >
                    <h3 className="font-headings text-lg font-bold text-primary">
                      {opt.name}
                    </h3>
                    <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                      {opt.desc}
                    </p>
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-headings text-2xl font-bold text-primary sm:text-3xl">
                Questions to Discuss With Your Psychiatrist
              </h2>
              <ul className="mt-4 space-y-2 text-foreground/85">
                <li>
                  Were my previous medication trials at an adequate dose for a
                  sufficient duration?
                </li>
                <li>
                  Could a comorbid condition (anxiety, bipolar disorder, ADHD,
                  substance use) be affecting my response?
                </li>
                <li>
                  Would a medication adjustment or augmentation strategy be
                  appropriate?
                </li>
                <li>Am I a candidate for TMS, SAINT®, or SPRAVATO®?</li>
                <li>
                  What are the risks, benefits, and time commitment for each
                  option?
                </li>
                <li>What is the plan if the next treatment does not work?</li>
              </ul>
            </div>

            {/* FAQ */}
            <div>
              <h2 className="font-headings text-2xl font-bold text-primary sm:text-3xl">
                Frequently Asked Questions
              </h2>
              <div className="mt-4 space-y-4">
                {faqs.map((faq, i) => (
                  <details
                    key={i}
                    className="rounded-xl border border-border bg-card p-5 shadow-soft"
                  >
                    <summary className="cursor-pointer font-headings text-base font-bold text-primary">
                      {faq.q}
                    </summary>
                    <p className="mt-3 text-sm sm:text-base text-foreground/85 leading-relaxed">
                      {faq.a}
                    </p>
                  </details>
                ))}
              </div>
            </div>

            <ClinicalReviewNote
              reviewedBy="Daniel Pietras, MD"
              lastUpdated="2026-11-03"
            />
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-br from-primary to-primary-deep py-12 text-center text-white">
          <div className="mx-auto max-w-2xl px-4">
            <h2 className="font-headings text-2xl font-bold sm:text-3xl">
              Talk With Our Team About Your Options
            </h2>
            <p className="mt-3 text-white/90">
              If depression treatment hasn&apos;t worked, a consultation can
              help you understand what may be appropriate for your situation.
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/contactus"
                className="inline-flex items-center justify-center rounded-lg bg-accent px-6 py-3 text-sm font-bold text-accent-foreground shadow-glow transition-all hover:opacity-90"
              >
                Request a Free Consultation
              </Link>
              <a
                href="tel:9548279793"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/30 px-6 py-3 text-sm font-bold text-white transition-all hover:bg-white/10"
              >
                <Phone className="h-4 w-4" /> Call 954-827-9793
              </a>
            </div>
          </div>
        </section>

        <RelatedPages heading="Related Resources" links={related} />
      </main>
      <Footer />
    </div>
  );
};

export default DepressionTreatmentNotWorking;
